import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './components/auth/AuthContext';
import { ThemeProvider } from './components/theme/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import VerifyEmail from './components/auth/VerifyEmail';
import { ProjectProvider } from './components/projects/ProjectProvider';
import { StudentProvider } from './components/students/StudentProvider';
import { ClassesProvider } from './components/classes/ClassesContext';
import { EnrollmentsProvider } from './components/enrollments/EnrollmentsProvider';
import { CustomFieldsProvider } from './components/classes/CustomFields';
import Sidebar from './components/Sidebar';
import MobileHeader from './components/MobileHeader';
import Dashboard from './components/Dashboard';
import ClassCatalog from './components/classes/ClassCatalog';
import ClassView from './components/classes/ClassView';
import AddClass from './components/classes/AddClass';
import CustomFields from './components/classes/CustomFields';
import StudentList from './components/students/StudentList';
import AddStudent from './components/students/AddStudent';
import EditStudent from './components/students/EditStudent';
import StudentView from './components/students/StudentView';
import Messenger from './components/students/Messenger';
import ProjectCatalog from './components/projects/ProjectCatalog';
import AddProject from './components/projects/AddProject';
import ProjectView from './components/projects/ProjectView';
import Enrollments from './components/enrollments/Enrollments';
import AddEnrollment from './components/enrollments/AddEnrollment';
import EditEnrollment from './components/enrollments/EditEnrollment';
import BulkEditEnrollments from './components/enrollments/BulkEditEnrollments';
import SchedulePlanner from './components/schedule/SchedulePlanner';
import Settings from './components/settings/Settings';
import UserProfile from './components/settings/UserProfile';
import EmailSettings from './components/settings/EmailSettings';
import OrganizationSettings from './components/settings/OrganizationSettings';
import StripeSettings from './components/settings/StripeSettings';
import Tools from './components/settings/Tools';
import Import from './components/settings/Import';
import TestDatePicker from './components/scheduling/TestDatePicker';
import PortalSettings from './components/pages/PortalSettings';
import PortalDraft from './components/pages/PortalDraft';
import PortalPage from './components/pages/PortalPage';
import PublicPortal from './components/pages/PublicPortal';
import PublicProject from './components/pages/PublicProject';
import PublicWaitlist from './components/pages/PublicWaitlist';
import LearningBlockList from './components/learning-blocks/LearningBlockList';
import AddEditLearningBlock from './components/learning-blocks/AddEditLearningBlock';
import CustomerIntelligence from './components/intelligence/CustomerIntelligence';
import RuleDetail from './components/intelligence/RuleDetail';
import RuleForm from './components/intelligence/RuleForm';
import AdminTools from './components/pages/AdminTools';
import PagesManagement from './components/pages/PagesManagement';
import PageEdit from './components/pages/PageEdit';
import PortalRedirect from './components/pages/PortalRedirect';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
        <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          
          {/* Public Portal Routes */}
          <Route path="/portal/:teacherId" element={<PortalRedirect />} />
          <Route path="/portal/:teacherId/enroll/:classId" element={<PublicPortal />} />
          <Route path="/portal/:teacherId/project/:projectId" element={<PublicProject />} />
          <Route path="/portal/:userId/:slug" element={<PortalPage />} />
          <Route path="/waitlist/:classId" element={<PublicWaitlist />} />

          {/* Draft Preview Route (Protected) */}
          <Route path="/portal/:teacherId/draft" element={
            <ProtectedRoute>
              <PortalDraft />
            </ProtectedRoute>
          } />
          
          {/* Protected Application Routes */}
          <Route path="/*" element={
            <ProtectedRoute>
              <CustomFieldsProvider>
                <ProjectProvider>
                  <StudentProvider>
                    <ClassesProvider>
                      <EnrollmentsProvider>
                        <div className="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
                          {/* Sidebar with mobile responsiveness */}
                          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                          
                          {/* Dynamic Main Content Area */}
                          <main className="flex-1 overflow-y-auto overflow-x-hidden">
                            <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
                            <div className="lg:hidden h-14" /> {/* Spacer for mobile header */}
                            <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    
                    {/* Schedule Routes */}
                    <Route path="/schedule" element={<SchedulePlanner />} />
                    
                    {/* Classes Routes */}
                    <Route path="/classes" element={<ClassCatalog />} />
                    <Route path="/classes/catalog" element={<ClassCatalog />} />
                    <Route path="/classes/add" element={<AddClass />} />
                    <Route path="/classes/edit/:id" element={<AddClass />} />
                    <Route path="/classes/view/:id" element={<ClassView />} />
                    <Route path="/classes/custom-fields" element={<CustomFields />} />
                    
                    {/* Students Routes */}
                    <Route path="/students" element={<StudentList />} />
                    <Route path="/students/list" element={<StudentList />} />
                    <Route path="/students/add" element={<AddStudent />} />
                    <Route path="/students/edit/:id" element={<AddStudent />} />
                    <Route path="/students/view/:id" element={<StudentView />} />
                    <Route path="/students/messenger" element={<Messenger />} />
                    
                    {/* Projects Routes */}
                    <Route path="/projects" element={<ProjectCatalog />} />
                    <Route path="/projects/catalog" element={<ProjectCatalog />} />
                    <Route path="/projects/add" element={<AddProject />} />
                    <Route path="/projects/edit/:id" element={<AddProject />} />
                    <Route path="/projects/view/:id" element={<ProjectView />} />

                    {/* Learning Blocks Routes */}
                    <Route path="/learning-blocks" element={<LearningBlockList />} />
                    <Route path="/learning-blocks/add" element={<AddEditLearningBlock />} />
                    <Route path="/learning-blocks/edit/:id" element={<AddEditLearningBlock />} />

                    {/* Customer Intelligence Routes */}
                    <Route path="/intelligence" element={<CustomerIntelligence />} />
                    <Route path="/intelligence/view/:id" element={<RuleDetail />} />
                    <Route path="/intelligence/edit/:id" element={<RuleForm />} />
                    <Route path="/intelligence/add" element={<RuleForm />} />

                    {/* Enrollments Routes */}
                    <Route path="/enrollments/add" element={<AddEnrollment />} />
                    <Route path="/enrollments/bulk-edit" element={<BulkEditEnrollments />} />
                    <Route path="/enrollments/edit/:id" element={<EditEnrollment />} />
                    <Route path="/enrollments" element={<Enrollments />} />
                    
                    {/* Portal Routes */}
                    <Route path="/portal" element={<PortalSettings />} />
                    <Route path="/portal-settings" element={<PortalSettings />} />
                    <Route path="/pages" element={<PagesManagement />} />
                    <Route path="/pages/:pageId/edit" element={<PageEdit />} />

                    {/* Admin Routes */}
                    <Route path="/admin" element={<AdminTools />} />

                              {/* Settings Routes */}
                              <Route path="/settings" element={<Settings />} />
                              <Route path="/settings/profile" element={<UserProfile />} />
                              <Route path="/settings/email" element={<EmailSettings />} />
                              <Route path="/settings/general" element={<Settings />} />
                              <Route path="/settings/organization" element={<OrganizationSettings />} />
                              <Route path="/settings/stripe" element={<StripeSettings />} />
                              <Route path="/settings/tools" element={<Tools />} />
                              <Route path="/settings/tools/import" element={<Import />} />

                              {/* Test Routes */}
                              <Route path="/test/datepicker" element={<TestDatePicker />} />
                            </Routes>
                          </main>
                        </div>
                      </EnrollmentsProvider>
                    </ClassesProvider>
                  </StudentProvider>
                </ProjectProvider>
              </CustomFieldsProvider>
            </ProtectedRoute>
          } />
        </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;